// @ts-nocheck
import { useMutation, useQuery } from "@tanstack/react-query";
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  MutationFunction,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
} from "@tanstack/react-query";
import type {
  StorefrontMagicLinkErrorResponse,
  StorefrontMagicLinkLoginResponse,
  StorefrontMagicLinkRequestResponse,
  StorefrontRequestMagicLink,
} from "../../models";
import { customInstance } from "../../custom-instance";

type SecondParameter<T extends (...args: any) => any> = Parameters<T>[1];

/**
 * Validate a magic link token and return JWT access/refresh tokens. If the token includes a session_uuid, the session will be claimed by the user.
 * @summary Login via magic link
 */
export const storefrontAuthOnboardingLoginRetrieve = (
  token: string,
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal
) => {
  return customInstance<StorefrontMagicLinkLoginResponse>(
    {
      url: `/api/storefront/auth/onboarding/login/${token}/`,
      method: "GET",
      signal,
    },
    options
  );
};

export const getStorefrontAuthOnboardingLoginRetrieveQueryKey = (
  token: string
) => {
  return [`/api/storefront/auth/onboarding/login/${token}/`] as const;
};

export const getStorefrontAuthOnboardingLoginRetrieveQueryOptions = <
  TData = Awaited<ReturnType<typeof storefrontAuthOnboardingLoginRetrieve>>,
  TError = StorefrontMagicLinkErrorResponse
>(
  token: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof storefrontAuthOnboardingLoginRetrieve>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof customInstance>;
  }
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getStorefrontAuthOnboardingLoginRetrieveQueryKey(token);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof storefrontAuthOnboardingLoginRetrieve>>
  > = ({ signal }) =>
    storefrontAuthOnboardingLoginRetrieve(token, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: !!token,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof storefrontAuthOnboardingLoginRetrieve>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type StorefrontAuthOnboardingLoginRetrieveQueryResult = NonNullable<
  Awaited<ReturnType<typeof storefrontAuthOnboardingLoginRetrieve>>
>;
export type StorefrontAuthOnboardingLoginRetrieveQueryError =
  StorefrontMagicLinkErrorResponse;

export function useStorefrontAuthOnboardingLoginRetrieve<
  TData = Awaited<ReturnType<typeof storefrontAuthOnboardingLoginRetrieve>>,
  TError = StorefrontMagicLinkErrorResponse
>(
  token: string,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof storefrontAuthOnboardingLoginRetrieve>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof storefrontAuthOnboardingLoginRetrieve>>,
          TError,
          Awaited<ReturnType<typeof storefrontAuthOnboardingLoginRetrieve>>
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof customInstance>;
  }
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useStorefrontAuthOnboardingLoginRetrieve<
  TData = Awaited<ReturnType<typeof storefrontAuthOnboardingLoginRetrieve>>,
  TError = StorefrontMagicLinkErrorResponse
>(
  token: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof storefrontAuthOnboardingLoginRetrieve>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof storefrontAuthOnboardingLoginRetrieve>>,
          TError,
          Awaited<ReturnType<typeof storefrontAuthOnboardingLoginRetrieve>>
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof customInstance>;
  }
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useStorefrontAuthOnboardingLoginRetrieve<
  TData = Awaited<ReturnType<typeof storefrontAuthOnboardingLoginRetrieve>>,
  TError = StorefrontMagicLinkErrorResponse
>(
  token: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof storefrontAuthOnboardingLoginRetrieve>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof customInstance>;
  }
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary Login via magic link
 */

export function useStorefrontAuthOnboardingLoginRetrieve<
  TData = Awaited<ReturnType<typeof storefrontAuthOnboardingLoginRetrieve>>,
  TError = StorefrontMagicLinkErrorResponse
>(
  token: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof storefrontAuthOnboardingLoginRetrieve>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof customInstance>;
  }
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = getStorefrontAuthOnboardingLoginRetrieveQueryOptions(
    token,
    options
  );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Send a magic link to the provided email address. If the email is new, a user account will be created. The magic link expires in 15 minutes.
 * @summary Request magic link
 */
export const storefrontAuthOnboardingRequestLinkCreate = (
  storefrontRequestMagicLink: StorefrontRequestMagicLink,
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal
) => {
  return customInstance<StorefrontMagicLinkRequestResponse>(
    {
      url: `/api/storefront/auth/onboarding/request-link/`,
      method: "POST",
      headers: { "Content-Type": "application/json" },
      data: storefrontRequestMagicLink,
      signal,
    },
    options
  );
};

export const getStorefrontAuthOnboardingRequestLinkCreateMutationOptions = <
  TError = StorefrontMagicLinkErrorResponse,
  TContext = unknown
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof storefrontAuthOnboardingRequestLinkCreate>>,
    TError,
    { data: StorefrontRequestMagicLink },
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof storefrontAuthOnboardingRequestLinkCreate>>,
  TError,
  { data: StorefrontRequestMagicLink },
  TContext
> => {
  const mutationKey = ["storefrontAuthOnboardingRequestLinkCreate"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof storefrontAuthOnboardingRequestLinkCreate>>,
    { data: StorefrontRequestMagicLink }
  > = (props) => {
    const { data } = props ?? {};

    return storefrontAuthOnboardingRequestLinkCreate(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type StorefrontAuthOnboardingRequestLinkCreateMutationResult =
  NonNullable<
    Awaited<ReturnType<typeof storefrontAuthOnboardingRequestLinkCreate>>
  >;
export type StorefrontAuthOnboardingRequestLinkCreateMutationBody =
  StorefrontRequestMagicLink;
export type StorefrontAuthOnboardingRequestLinkCreateMutationError =
  StorefrontMagicLinkErrorResponse;

/**
 * @summary Request magic link
 */
export const useStorefrontAuthOnboardingRequestLinkCreate = <
  TError = StorefrontMagicLinkErrorResponse,
  TContext = unknown
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof storefrontAuthOnboardingRequestLinkCreate>>,
    TError,
    { data: StorefrontRequestMagicLink },
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseMutationResult<
  Awaited<ReturnType<typeof storefrontAuthOnboardingRequestLinkCreate>>,
  TError,
  { data: StorefrontRequestMagicLink },
  TContext
> => {
  const mutationOptions =
    getStorefrontAuthOnboardingRequestLinkCreateMutationOptions(options);

  return useMutation(mutationOptions);
};
